import { getIdOrNull, requestWithPromise } from '..'
import { getDataOrDefault, HttpMethods, makeQueryString } from '../apiHelper'
import apiClient from './apiClient'

export const createUser = (user, newUser, disabledErrorHandler = true, followError = true) => {
  return requestWithPromise(
    '/api/users',
    HttpMethods.POST,
    newUser,
    user,
    disabledErrorHandler,
    followError
  )
}

export const deleteUser = (user, userToDelete) => apiClient.delete(user, `api/User/${getIdOrNull(userToDelete)}`)

export const getRoles = user => {
  return requestWithPromise(
    '/api/roles',
    HttpMethods.GET,
    null,
    user
  )
}

export const getUsers = (filter, user, excludeAdmins = 0, role = null) => {
  let url = `/users?excludeAdminUsers=${excludeAdmins}&newFilter=${filter}`

  if (typeof role === 'string') {
    url += '&role=' + role
  }

  return requestWithPromise(
    url,
    HttpMethods.GET,
    null,
    user
  )
}

export const getMe = token => {
  return apiClient.get({ token, switchUser: null }, 'user/me')
}

/**
 * Copies onto a profile the identity fields owned by the account (`username`, `email`,
 * `firstname`, `lastname`, `phone`, `language`) when the profile no longer carries them — since
 * the multi-profile rework, the identity lives on the account (AccountCache). Also exposes the
 * account on `.account`.
 *
 * @param {object} profile
 * @param {?object} account
 * @returns {object}
 */
export const mergeAccountIdentity = (profile, account = null) => {
  if (!profile) { return profile }

  const merged = { ...profile, account: account ?? null }

  if (account) {
    // The account has no username anymore: the email is the only identifier (the `username` key
    // is kept for backward compatibility and holds the email).
    if (merged.username == null) { merged.username = account.email }
    if (merged.email == null) { merged.email = account.email }
    if (merged.firstname == null) { merged.firstname = account.firstname }
    if (merged.lastname == null) { merged.lastname = account.lastname }
    if (merged.phone == null) { merged.phone = account.phone }
    if (merged.language == null) {
      merged.language = Array.isArray(account.languages) ? account.languages[0] : undefined
    }
  }

  return merged
}

/**
 * Normalises the `/user/me` response (new `{ data, account, profiles }` contract) into a `user`
 * object the rest of the app still understands: identity pulled from the account, account exposed
 * on `user.account`, profile list on `user.profiles`.
 *
 * `/user/me` no longer embeds the `affiliatedSectors`, `contactPersonSectors` and
 * `supervisedSectors` collections: they are fetched on demand from their own endpoints by the
 * screens that need them. Only `affiliatedSectorsCount` is still carried, because AppManager reads
 * it on its very first render to tell a nurse linked to no care unit to ask their administrator for
 * an affiliation. It is defaulted to 0 so a frontend deployed ahead of the backend still renders —
 * every nurse would then see that notice for nothing, so deploy the backend first.
 *
 * @param {{ data: object, account: ?object, profiles: ?Array }} json raw /user/me response
 * @returns {object} the normalised user object
 */
export const normalizeCurrentUser = json => {
  const account = json.account || null
  const profiles = Array.isArray(json.profiles) ? json.profiles : []
  const user = mergeAccountIdentity({ ...json.data }, account)
  user.profiles = profiles
  user.affiliatedSectorsCount = user.affiliatedSectorsCount ?? 0

  // `roles` is required to resolve the user context. When the active profile does not carry them,
  // they are read from the matching entry of the profile list.
  if (!Array.isArray(user.roles)) {
    const activeProfile = profiles.find(profile => profile && profile.id === user.id)
    user.roles = activeProfile && Array.isArray(activeProfile.roles) ? activeProfile.roles : []
  }

  return user
}

/**
 * Some endpoints wrap their response in `{ data: … }`: unwrap it when needed to get back to the
 * `{ account, profiles }` object.
 */
const unwrapAccountProfiles = json => {
  if (json && !Array.isArray(json.profiles) && json.data && Array.isArray(json.data.profiles)) {
    return json.data
  }

  return json
}

/**
 * Rebuilds a table-ready "user" object from the response of the update endpoints (new
 * `{ account, profiles }` contract, no `data` field): the target profile is looked up in the
 * `profiles` list and gets the account identity attached.
 *
 * @param {{ account: ?object, profiles: ?Array }} json  PATCH /api/users/{id} response
 * @param {(number|string)} profileId  id of the updated profile
 * @returns {object|null}  null when the profile is absent from the response
 */
export const buildUpdatedUserFromResponse = (json, profileId) => {
  const body = unwrapAccountProfiles(json)
  const profiles = Array.isArray(body?.profiles) ? body.profiles : []
  const profile = profiles.find(p => p && String(p.id) === String(profileId)) ?? null

  return profile ? mergeAccountIdentity(profile, body?.account ?? null) : null
}

/**
 * Same as `buildUpdatedUserFromResponse` for a creation: the new profile is the one of the
 * response whose id the front does not know yet.
 *
 * @param {{ account: ?object, profiles: ?Array }} json  POST /api/users response
 * @param {Array<(number|string)>} knownProfileIds  ids of the already displayed profiles
 * @returns {object|null}
 */
export const buildCreatedUserFromResponse = (json, knownProfileIds = []) => {
  const body = unwrapAccountProfiles(json)
  const known = new Set(knownProfileIds.map(String))
  const profiles = Array.isArray(body?.profiles) ? body.profiles : []
  const profile = profiles.find(p => p && !known.has(String(p.id))) ?? null

  return profile ? mergeAccountIdentity(profile, body?.account ?? null) : null
}

/**
 * Identifies, in the { account, profiles } response of a creation OR a restoration, the profile
 * the current operation actually acted on — for the views that insert the row optimistically
 * (school, institution) instead of refetching the list.
 *
 * The response holds ALL the profiles of the account, including sibling profiles attached to OTHER
 * organizations (multi-profile). Taking the "first profile whose id is not displayed yet" (see
 * buildCreatedUserFromResponse) therefore inserts the wrong profile as soon as an account spans
 * several organizations, and any later edit of the row PATCHes that wrong profile. So the search is
 * restricted to the unknown profiles attached to the current organization (school or institution)
 * and, when a role is given, sharing that role; the most recent one wins (highest id = the one just
 * created/restored). The narrowing is progressive: org+role → org → (defensively) any unknown
 * profile, to stay robust if the organization or the role was not serialized.
 *
 * @param {{ account: ?object, profiles: ?Array }} json  POST /api/users or PATCH /user/restore response
 * @param {{ knownProfileIds?: Array<(number|string)>, schoolId?: ?string, institutionId?: ?string, role?: ?string }} ctx
 * @returns {object|null}
 */
export const pickAffectedProfile = (json, { knownProfileIds = [], schoolId = null, institutionId = null, role = null } = {}) => {
  const body = unwrapAccountProfiles(json)
  const known = new Set(knownProfileIds.map(String))
  const unknown = (Array.isArray(body?.profiles) ? body.profiles : []).filter(p => p && !known.has(String(p.id)))

  const inOrg = p => {
    if (schoolId) { return p.school != null && String(p.school.id) === String(schoolId) }
    if (institutionId) { return Array.isArray(p.institutions) && p.institutions.some(i => String(i.id) === String(institutionId)) }

    return true
  }
  const hasRole = p => role == null || (Array.isArray(p.roles) && p.roles.includes(role))
  const newest = list => [...list].sort((a, b) => Number(b.id) - Number(a.id))[0] ?? null

  const profile =
    newest(unknown.filter(p => inOrg(p) && hasRole(p))) ??
    newest(unknown.filter(inOrg)) ??
    newest(unknown)

  return profile ? mergeAccountIdentity(profile, body?.account ?? null) : null
}

export const getPaginateUsers = (user, parameters) => {
  return requestWithPromise(
    `/api/users${makeQueryString(parameters)}`,
    HttpMethods.GET,
    null,
    user
  )
}

export const addSupervisorStudents = (user, body, currentUser) => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(user)}/supervised-students`,
    HttpMethods.POST,
    body,
    currentUser
  )
}

export const deleteSupervisorStudents = (user, supervisor, student) => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(supervisor)}/supervised-students/${getIdOrNull(student)}`,
    HttpMethods.DELETE,
    null,
    user
  )
}

export const getSupervisorStudents = (user, currentUser, valueSearch = '') => {
  const search = typeof valueSearch !== 'undefined' ? `?search=${valueSearch}` : ''

  return requestWithPromise(
    `/api/users/${getIdOrNull(user)}/supervised-students${search}`,
    HttpMethods.GET,
    null,
    currentUser
  ).then(getDataOrDefault({}))
}

export const getCountSupervisorStudents = user => {
  return requestWithPromise(
    '/user/supervised-students-count',
    HttpMethods.GET,
    null,
    user
  ).then(getDataOrDefault({}))
}

export const getUserOptins = user => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(user)}/optins`,
    HttpMethods.GET,
    null,
    user
  )
}

export const createUserOptin = (optin, user) => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(user)}/optins`,
    HttpMethods.POST,
    optin.toRequestBody(),
    user
  )
}

export const updateUser = (user, body, disabledErrorHandler = false, followError = false) => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(body)}`,
    HttpMethods.PATCH,
    body,
    user,
    disabledErrorHandler,
    followError
  )
}

/**
 * "Organization admin" reset: sets the password of the account behind the target profile directly,
 * with no email and no action from the user. The route is only open to school admins (ROLE_ENCODER)
 * and institution admins (ROLE_HOSPITAL_ADMIN, which includes ROLE_INSTITUTION_GROUP_ADMIN), and
 * the scope is checked again target by target on the server (UserVoter::RESET_PASSWORD).
 *
 * The target is a PROFILE id (`User`), not the account uuid: for a student, that is its
 * `student.user`. Success returns an empty body (JSON `null`), errors reject with the Response.
 *
 * @param {object} user the current user (token holder)
 * @param {(object|number)} profile the target profile, or its id
 * @param {string} password 8+ characters, at least one lowercase, one uppercase and one digit
 */
export const changeUserPassword = (user, profile, password) => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(profile)}/change-password`,
    HttpMethods.POST,
    { password },
    user,
    true,
    true
  )
}

export const updateUserOptin = (optin, user) => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(user)}/optins/${getIdOrNull(optin)}`,
    HttpMethods.PATCH,
    optin.toRequestBody(),
    user
  )
}

export const getUserSectors = (user, targetUser, parameters = {}) => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(targetUser)}/sectors${makeQueryString(parameters)}`,
    HttpMethods.GET,
    null,
    user
  )
}

export const getUserSupervisedSectors = (selectedUser, user, parameters = {}) => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(selectedUser)}/supervised-sectors${makeQueryString(parameters)}`,
    HttpMethods.GET,
    null,
    user
  )
}

export const setUserSupervisedSectors = (sectors, supervisorUser, user) => {
  return requestWithPromise(
    `/user/set-supervisor-sectors/${getIdOrNull(supervisorUser)}`,
    HttpMethods.POST,
    { sectors: sectors.map(s => s.id) },
    user
  )
}

/**
 * Restores a deleted profile (same organization only): the backend returns { account, profiles },
 * or 403 RESTORE_USER_PERMISSION_DENIED when the deleted profile belongs to another organization
 * (which then has to create its own sibling profile through POST /api/users). The email-based
 * "transfer" mechanism between organizations no longer exists.
 */
export const restoreUserByEmail = (user, userToRestore) => {
  return apiClient.patch(user, 'user/restore', { email: userToRestore.email })
}

export const getTutoredSectors = (user, targetUser) => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(targetUser)}/tutored-sectors`,
    HttpMethods.GET,
    null,
    user
  )
}

export const linkTutoredSectors = (user, targetUser, sectors) => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(targetUser)}/tutored-sectors`,
    HttpMethods.POST,
    { ids: sectors },
    user
  )
}

export const unLinkTutoredSectors = (user, targetUser, sectors) => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(targetUser)}/tutored-sectors${makeQueryString({ ids: sectors })}`,
    HttpMethods.DELETE,
    null,
    user
  )
}

export const getUserUnreadMessages = user => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(user)}/unread-messages`,
    HttpMethods.GET,
    null,
    user
  )
}

export const readAllUserMessages = user => {
  return requestWithPromise(
    '/internship-message/read-all',
    HttpMethods.POST,
    {},
    user
  )
}

const USER_COORDINATED_SECTIONS_PARAMS = { search: '' }
export const getUserCoordinatedSections = (user, targetUser, parameters = USER_COORDINATED_SECTIONS_PARAMS) => {
  return apiClient.get(user, `api/users/${getIdOrNull(targetUser)}/coordinated-sections`, parameters)
}

export const linkUserToSections = (user, targetUser, sectionIds) => {
  return apiClient.post(user, `api/users/${getIdOrNull(targetUser)}/coordinated-sections`, { ids: sectionIds })
}

export const unlinkSectionFromUser = (user, targetUser, section) => {
  return apiClient.delete(user, `api/users/${getIdOrNull(targetUser)}/coordinated-sections/${getIdOrNull(section)}`)
}

export const getInternshipRequestByToken = (token) => apiClient.get(null, `api/internal-tokens/${token}/individual-internship-request`)
